"use client";

import React from "react";
import { ArrowRight, ArrowUpRight, CircleCheck as CheckCircle2, Cpu, Layers, Sparkles, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
interface SolutionItem {
  id: string;
  badge: string;
  title: string;
  description: string;
  specs: string;
  href: string;
}
const solutions: SolutionItem[] = [{
  id: "pat-solutions",
  badge: "Integrated Systems",
  title: "Process Analytical Technology Solutions",
  description: "Comprehensive in-line, on-line, and at-line analytical setups designed for blend uniformity, drying endpoint, and real-time release testing.",
  specs: "Continuous verification • Non-destructive • 21 CFR Part 11 compliant",
  href: "/ProcessAnalyticalTechnologyPage"
}, {
  id: "bruker-pat",
  badge: "High-Resolution FT-NIR & Raman",
  title: "Bruker PAT Spectroscopic Family",
  description: "Robust process spectrometers including MATRIX-F II, BEAM, and Process Guardian for harsh manufacturing environments and reactor monitoring.",
  specs: "Multi-point fiber coupling • Solid & liquid sampling • IP65 rated",
  href: "/UserPatPage"
}, {
  id: "viavi-micronir",
  badge: "Ultra-Compact NIR",
  title: "VIAVI MicroNIR Family",
  description: "Miniature, fit-for-purpose NIR spectrometers for rotary blenders, fluid beds, and portable field analysis with wireless data transfer.",
  specs: "PAT-W & PAT-U units • ATEX / IECEx certified • Low maintenance",
  href: "/ViaviMicroNirPage"
}];
const highlights: string[] = ["Validated hardware & qualification packages (IQ/OQ/PQ)", "Direct chemometric model development and tech transfer", "Dedicated field support across Ireland and the UK"];
export default function PatSolutions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: [0.215, 0.61, 0.355, 1]
      }
    }
  };
  return <section id="patsolutionsoverview" data-nav="light" className="w-full bg-background py-16 md:py-24 border-b border-border">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left Column: 5 Cols (Contextual Info) */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-80px"
        }} transition={{
          duration: 0.5,
          ease: [0.215, 0.61, 0.355, 1]
        }} className="lg:col-span-5 flex flex-col justify-between space-y-6 lg:sticky lg:top-24">
            <div className="space-y-4">
              <Badge variant="outline" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-secondary text-secondary-foreground border-border/80 rounded-full">
                <Sparkles className="size-3 text-primary mr-1" />
                Process Analytics
              </Badge>

              <h2 className="text-3xl md:text-4xl lg:text-4xl font-default font-bold text-foreground tracking-tight leading-tight">
                Real-time process monitoring from development to full-scale
                manufacturing
              </h2>

              <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
                Streamline batch release, prevent failed runs, and gain deep
                process understanding with proven PAT spectroscopic solutions
                engineered for regulated production facilities.
              </p>
            </div>

            {/* Feature Highlights */}
            <div className="pt-2 border-t border-border space-y-3">
              <p className="text-xs font-semibold text-foreground tracking-wider uppercase">
                Turnkey Implementation
              </p>
              <div className="space-y-2.5">
                {highlights.map((highlight, hIndex) => <div key={hIndex} data-index={hIndex} className="flex items-start gap-3 text-sm text-foreground/90 font-sans">
                    <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                    <span>{highlight}</span>
                  </div>)}
              </div>
            </div>

            {/* CTAs */}
            <div className="pt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button asChild size="lg" className="shadow-sm rounded-md font-medium">
                <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                  Discuss Your Application
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-md">
                <Link to="/SolutionsPage">
                  View All Solutions
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: 7 Cols (Stacked Link Cards) */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: "-60px"
        }} className="lg:col-span-7 flex flex-col gap-5">
            {solutions.map((item, index) => <motion.div key={item.id} data-index={index} variants={itemVariants} className="group relative">
                <Link to={item.href} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-xl">
                  <Card className="relative overflow-hidden border border-border bg-card hover:border-primary/50 hover:shadow-md transition-all duration-300 py-6 px-1">
                    {/* Subtle left accent bar on hover */}
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary scale-y-0 group-hover:scale-y-100 transition-transform duration-300 origin-top" />

                    <CardHeader className="gap-2 pb-2">
                      <div className="flex items-center justify-between gap-2">
                        <Badge variant="secondary" className="text-xs font-medium bg-secondary text-secondary-foreground border-transparent px-2.5 py-0.5 rounded-md">
                          {item.badge}
                        </Badge>
                        <div className="size-8 rounded-full bg-secondary group-hover:bg-primary group-hover:text-primary-foreground flex items-center justify-center text-muted-foreground transition-colors duration-300">
                          <ArrowUpRight className="size-4" />
                        </div>
                      </div>

                      <CardTitle className="text-xl md:text-2xl font-default font-semibold text-card-foreground group-hover:text-primary transition-colors duration-200 mt-1">
                        {item.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4">
                      <CardDescription className="text-sm md:text-base text-muted-foreground leading-relaxed font-sans">
                        {item.description}
                      </CardDescription>

                      <div className="pt-3 border-t border-border/60 flex items-center gap-2 text-xs text-muted-foreground font-sans">
                        <Cpu className="size-3.5 text-primary shrink-0" />
                        <span className="truncate">{item.specs}</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>)}

            {/* Bottom Assistance banner card */}
            <div className="p-4 rounded-xl bg-secondary/70 border border-border flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="size-9 rounded-md bg-background border border-border flex items-center justify-center shrink-0">
                  <Zap className="size-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Need method development or feasibility testing?
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Our team provides hands-on spectroscopic application testing.
                  </p>
                </div>
              </div>
              <Button asChild variant="ghost" size="sm" className="shrink-0 text-primary font-medium hover:text-primary">
                <Link to="/FeasibilityStudiesPage" className="inline-flex items-center gap-1">
                  Learn more
                  <ArrowRight className="size-3.5" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}